// @ts-check
import { useState, useEffect } from 'react';
import {
  Container,
  Button,
  IconButton,
  Typography,
  Box,
  Avatar,
  Paper,
  Grid,
  SelectChangeEvent,
} from '@mui/material';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  updateProfile,
  deleteUser,
  getSelf,
} from '../../../repository/UserDAO';
import PermissionsModal from '../../../components/modals/permissions/PermissionsModal';
import { isAdmin } from '../../../components/routing/UserRoutes/AcceptedRoles';
import ConfirmModal from '../../../components/modals/utils/ConfirmModal';
import Loading from '../../../components/loading/Loading';
import EditProfileNameModal from './modals/EditProfileNameModal';
import LoadingData from '../../../components/loading/LoadingData';
import LoadingSpinner from '../../../components/loading/LoadingSpinner';
import LanguageSelector from '../../../components/selectors/LanguageSelector';
import { useUserUpdaterProvider } from '../../../context_providers/user_context/UserUpdaterProvider';
import { useOrganizationUpdaterProvider } from '../../../context_providers/organization_context/OrganizationProvider';
import { useAuth0 } from '@auth0/auth0-react';
import { useToast } from '../../../context_providers/toast_context/ToastContext';
import { useLoading } from '../../../context_providers/loading_context/LoadingContext';
import {
  ErrorStringKeys,
  StringKeys,
  SuccessStringKeys,
} from '../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import ArticleIcon from '@mui/icons-material/Article';
import COLORS from '../../../resources/style/Themes';
import { Download } from '@mui/icons-material';
import { getAllEmergencyByID } from '../../../repository/EmergencyDAO';
import { downloadPDF } from '../../../reports/utils/ReportUtils';
import { UserExportedData } from '../../../reports/UserExportedData/UserExportedData';
import UpdateOrganizationModal from './modals/UpdateOrganizationModal';
import UpdateGroupModal from './modals/UpdateGroupModal';
import packageJson from '../../../../package.json';
import SupportLinks from '../../../enums/SupportLinks';
import {
  EmergencyContactInfoOnAzure,
  EmergencyEmailContact,
  instanceOfMiniOrganization,
  JoinRequestCreatedResponse,
  MiniOrganization,
  OrganizationOnAzure,
  OrgUserSelfUpdate,
  PendingJoinRequestResponse,
} from '../../../../generated-client/models';
import {
  getMyPendingJoinRequest,
  cancelMyJoinRequest,
} from '../../../repository/JoinRequestsDAO';
import {
  getOrgEmergencyContacts,
  getGroupEmergencyContacts,
} from '../../../repository/OrganizationDAO';
import { OrgUser } from '../../../models/User/OrgUser';

export interface EmergencyNumbers {
  medical: string;
  fire: string;
  police: string;
}

export interface EmergencyDetails {
  emergency_id: string;
  emergency_numbers: EmergencyNumbers;
  incident: string;
  creation_timestamp: number;
  creator_name: string; // composed from first/last
  creator_phone: string;
  resolved_timestamp: number | null | undefined;
  // You copy through whatever the API returns; we don't inspect it here.
  // Keep it broad unless you know the exact shape.
  users: EmergencyContactInfoOnAzure[];
}

export interface EmergencyUserMessage {
  message_kind: string;
  content?: string | null;
  sent_timestamp?: number;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface EmergencyUserLocation {
  address?: string | null;
  creation_timestamp?: number;
  degrees_bearing?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  meters_per_second_speed?: number | null;
  sent_timestamp?: number | null;
}

// --- aggregate type used by your code ---

export interface EmergencyData {
  emergencyDetails: EmergencyDetails | null;
  userMessages: EmergencyUserMessage[];
  userLocations: EmergencyUserLocation[];
}

// Map by emergencyId (stringified keys from Object.keys(...))
export type EmergencyDataMap = Record<string, EmergencyData>;

const versionNumber = packageJson.version;

// Button styles in the profile component
const actionButtonStyle = {
  borderRadius: 3,
  backgroundColor: COLORS.BLUE_CRAYOLA,
  transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#2b64af',
    transform: 'translateY(-2px)',
  },
};

type ResponderRow = { first_name: string; last_name: string };
type EmailRow = { name: string; email: string };

const toResponderRows = (
  contactAuthIds: { [key: string]: string } | undefined,
  authIdToUser: Map<string, OrgUser>,
): ResponderRow[] => {
  if (!contactAuthIds) return [];
  return Object.values(contactAuthIds)
    .map((authId) => {
      const user = authIdToUser.get(authId);
      return {
        first_name: user?.first_name ?? 'Unknown',
        last_name: user?.last_name ?? '',
      };
    })
    .sort((a, b) => {
      const byLast = a.last_name.localeCompare(b.last_name);
      return byLast !== 0 ? byLast : a.first_name.localeCompare(b.first_name);
    });
};

const toEmailRows = (
  emails: { [key: string]: EmergencyEmailContact } | undefined,
): EmailRow[] => {
  if (!emails) return [];
  return Object.values(emails)
    .map(({ name, email }) => ({ name, email }))
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
};

export const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const { orgUser, userHistory, userPrivateSettings } =
    useUserUpdaterProvider();
  const { organization, getUsersGroup, groups } =
    useOrganizationUpdaterProvider();
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();
  const { logout } = useAuth0();
  const [loadingAPIData, setLoadingAPIData] = useState(false);
  const [groupName, setGroupName] = useState('');
  const { setPageLoading } = useLoading();
  const { t, i18n } = useTranslation();
  const defaultLanguage = 'en-US';
  const [language, setLanguage] = useState(() => {
    const currentLang = i18n.language || defaultLanguage;
    return currentLang.startsWith('en') ? 'en-US' : currentLang;
  });
  const [showUpdateOrgModal, setShowUpdateOrgModal] = useState(false);
  const [showUpdateGroupModal, setShowUpdateGroupModal] = useState(false);
  const [pendingJoinRequest, setPendingJoinRequest] = useState<
    PendingJoinRequestResponse | JoinRequestCreatedResponse | null
  >(null);

  // Check for existing pending join request on mount
  useEffect(() => {
    const checkPendingRequest = async () => {
      try {
        const pendingRequest = await getMyPendingJoinRequest();
        if (pendingRequest) {
          setPendingJoinRequest(pendingRequest);
        }
      } catch {
        // No pending request or error - that's fine
        setPendingJoinRequest(null);
      }
    };
    checkPendingRequest();
  }, []);

  const handlePendingApproval = (joinRequest: JoinRequestCreatedResponse) => {
    setPendingJoinRequest(joinRequest);
  };

  const handleCancelRequest = async () => {
    setLoadingAPIData(true);
    try {
      await cancelMyJoinRequest();
      setPendingJoinRequest(null);
      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_CANCELLED_JOIN_REQUEST),
      );
    } catch {
      showErrorToast(t(ErrorStringKeys.FAILED_TO_CANCEL_JOIN_REQUEST));
    } finally {
      setLoadingAPIData(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const saveUserName = async (updatedName: OrgUserSelfUpdate) => {
    setShowEditProfileModal(false);
    setLoadingAPIData(true);
    Object.assign(updatedName, {
      org_code: organization?.org_code,
      phone_number: orgUser?.phone_number,
      org_user_azure_id: orgUser?.id,
    });

    try {
      await updateProfile(updatedName);
      showSuccessToast(t(SuccessStringKeys.SUCCESSFULLY_UPDATED_PROFILE));
    } catch {
      showErrorToast(t(ErrorStringKeys.FAILED_TO_UPDATE_PROFILE));
    } finally {
      setLoadingAPIData(false);
    }
  };

  const logoutWithRedirect = () =>
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

  const deleteProfile = async () => {
    try {
      if (!organization || !orgUser || !userHistory || !userPrivateSettings) {
        console.error('Missing an important data for deleting user');
        throw new Error();
      }

      await deleteUser(
        organization?.org_code,
        orgUser?.id,
        userHistory?.id,
        userPrivateSettings?.id,
        organization?.id,
      );
      logoutWithRedirect();
    } catch {
      showErrorToast(t(ErrorStringKeys.FAILED_TO_DELETE_PROFILE));
    }
  };

  const changeLanguage = (lng: string) => {
    const normalizedLang = lng.startsWith('en') ? 'en-US' : lng;
    i18n.changeLanguage(normalizedLang);
    setLanguage(normalizedLang);
  };

  useEffect(() => {
    if (orgUser && userPrivateSettings && organization) {
      const group = getUsersGroup(organization, orgUser.auth_id);
      console.log('Returned group name:', group);
      setGroupName(group);
      setLoading(false);
      setPageLoading(false);
    }
  }, [
    orgUser,
    userPrivateSettings,
    organization,
    getUsersGroup,
    setPageLoading,
  ]);

  const downloadMyData = async () => {
    try {
      setLoadingAPIData(true);
      const data = await getSelf();

      const userAuthId = data.org_user.auth_id;
      const userData = {
        first_name: data.org_user.first_name,
        last_name: data.org_user.last_name,
        auth_id: userAuthId,
        org_code: data.org_user.org_code,
        phone_number: data.org_user.phone_number,
        role: data.org_user.role,
        auto_add_contacts: data.org_user.auto_add_contacts,
      };

      const currentUserGroupName = organization
        ? getUsersGroup(organization, data.org_user.auth_id)
        : '';
      const groupId = currentUserGroupName
        ? (organization?.groups?.[currentUserGroupName]?.group_id ?? null)
        : null;

      const [orgRes] = await Promise.allSettled([getOrgEmergencyContacts()]);

      let groupContacts = undefined;
      let groupFetchFailed = false;
      if (groupId) {
        try {
          groupContacts = await getGroupEmergencyContacts(groupId);
        } catch {
          groupFetchFailed = true;
        }
      }

      const authIdToUser = new Map<string, OrgUser>();
      (groups ?? []).forEach((g) =>
        (g.group_value ?? []).forEach((u) => {
          if (!authIdToUser.has(u.auth_id)) authIdToUser.set(u.auth_id, u);
        }),
      );

      const emergencyContacts = {
        group: {
          groupName: currentUserGroupName,
          unavailable: groupFetchFailed,
          responders: toResponderRows(
            groupContacts?.contact_auth_ids,
            authIdToUser,
          ),
          emailContacts: toEmailRows(groupContacts?.contact_email_addresses),
        },
        company: {
          unavailable: orgRes.status === 'rejected',
          responders: toResponderRows(
            orgRes.status === 'fulfilled'
              ? orgRes.value.contact_auth_ids
              : undefined,
            authIdToUser,
          ),
          emailContacts: toEmailRows(
            orgRes.status === 'fulfilled'
              ? orgRes.value.contact_email_addresses
              : undefined,
          ),
        },
      };

      const observedEmergencies = data.user_history?.observed_emergencies || {};
      const emergencyPromises: Array<
        Promise<Record<string, EmergencyData> | null>
      > = Object.keys(observedEmergencies).map(async (emergencyId) => {
        try {
          const emergencyObj = await getAllEmergencyByID(
            emergencyId,
            data.org_user.org_code,
          );
          const emergencyData: EmergencyData = {
            emergencyDetails: null,
            userMessages: [],
            userLocations: [],
          };

          const { emergency, emergency_messages, locations } = emergencyObj;

          if (emergency && emergency.emergency_id) {
            emergencyData.emergencyDetails = {
              emergency_id: emergency.emergency_id,
              emergency_numbers: emergency.emergency_numbers || {
                medical: '911',
                fire: '911',
                police: '911',
              },
              incident: emergency.incident,
              creation_timestamp: emergency.creation_timestamp,
              creator_name: `${emergency.creator_first_name} ${emergency.creator_last_name}`,
              creator_phone: emergency.creator_phone_number,
              resolved_timestamp: emergency.resolved_timestamp,
              users: emergency.users,
            };
          }

          if (Array.isArray(emergency_messages)) {
            emergency_messages
              .filter((msg) => msg.sender_auth_id === userAuthId)
              .forEach((msg) => {
                emergencyData.userMessages.push({
                  message_kind: msg.message_kind,
                  content: msg.content,
                  sent_timestamp: msg.sent_timestamp,
                  address: msg.address,
                  latitude: msg.latitude,
                  longitude: msg.longitude,
                });
              });
          }

          if (Array.isArray(locations)) {
            locations
              .filter((loc) => loc.user_auth_id === userAuthId)
              .forEach((loc) => {
                emergencyData.userLocations.push({
                  creation_timestamp: loc.creation_timestamp,
                  degrees_bearing: loc.degrees_bearing,
                  latitude: loc.latitude,
                  longitude: loc.longitude,
                  meters_per_second_speed: loc.meters_per_second_speed,
                });
              });
          }
          return {
            [emergencyId]: emergencyData,
          };
        } catch {
          return null;
        }
        return null;
      });

      const emergencyDetails = await Promise.all(emergencyPromises);
      const formattedEmergencies = emergencyDetails
        .filter((e) => e !== null)
        .reduce((acc, curr) => ({ ...acc, ...curr }), {});

      const doc = UserExportedData(
        userData,
        formattedEmergencies,
        emergencyContacts,
        t,
      );
      downloadPDF(
        doc,
        `Safeguard ${t(StringKeys.PERSONAL_INFORMATION)} - ${userData.first_name} ${userData.last_name}`,
      );
    } catch (error) {
      console.error('Error downloading user data:', error);
      showErrorToast(t(ErrorStringKeys.FAILED_TO_RETRIEVE_YOUR_DATA));
    } finally {
      setLoadingAPIData(false);
    }
  };

  const getGroupNames = (
    organization: MiniOrganization | OrganizationOnAzure,
  ) => {
    if (instanceOfMiniOrganization(organization)) {
      return organization.all_group_names;
    } else if (organization.groups) {
      return Object.keys(organization.groups);
    }
    return [];
  };

  const renderData = () => {
    return (
      <Box
        sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: 1200, margin: '0 auto' }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 0 },
            mb: 3,
            borderRadius: '16px',
            color: COLORS.SAFEGUARD_BLUE,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid
              size="grow"
              sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Avatar
                sx={{
                  bgcolor: COLORS.SAFEGUARD_BLUE,
                  color: 'white',
                  width: 48,
                  height: 48,
                  fontWeight: 'bold',
                }}
              >
                {`${orgUser?.first_name?.charAt(0) || ''}${orgUser?.last_name?.charAt(0) || ''}`}
              </Avatar>

              <Typography variant={'h5'} sx={{ fontWeight: 500 }}>
                {orgUser?.first_name} {orgUser?.last_name}
                <IconButton
                  sx={{
                    mt: -0.5,
                    ml: 1,
                    color: COLORS.SAFEGUARD_BLUE,
                    transition:
                      'color 0.2s ease-in-out, transform 0.2s ease-in-out',
                    '&:hover': {
                      color: COLORS.BLUE_CRAYOLA,
                      transform: 'scale(1.2)',
                    },
                  }}
                  onClick={() => setShowEditProfileModal(true)}
                >
                  <EditIcon />
                </IconButton>
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        <Grid container spacing={3}>
          {/* Organization Information */}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Paper elevation={3} sx={{ borderRadius: '12px', height: '100%' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: COLORS.TABLE_HEADER_GRAY,
                  color: COLORS.SAFEGUARD_BLUE,
                  borderRadius: '12px 12px 0 0',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                <BusinessIcon />
                <Typography variant="h6">
                  {t(StringKeys.ORGANIZATION_DETAILS)}
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t(StringKeys.ORGANIZATION)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ mt: 0.5, color: COLORS.SAFEGUARD_BLUE }}
                    >
                      {organization?.company_name}
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t(StringKeys.ORGANIZATION_CODE)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ mt: 0.5, color: COLORS.SAFEGUARD_BLUE }}
                    >
                      {organization?.org_code}
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t(StringKeys.GROUP_NAME)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ mt: 0.5, color: COLORS.SAFEGUARD_BLUE }}
                    >
                      {groupName}
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    {pendingJoinRequest ? (
                      <Box
                        sx={{
                          backgroundColor: COLORS.TABLE_HEADER_GRAY,
                          p: 2,
                          borderRadius: 3,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 'bold',
                            color: COLORS.SAFEGUARD_BLUE,
                            mb: 1,
                          }}
                        >
                          {t(StringKeys.AWAITING_ADMINISTRATOR_APPROVAL)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {t(StringKeys.YOU_REQUESTED_TO_JOIN_ORGANIZATION, {
                            companyName: pendingJoinRequest.company_name,
                            requestedDate: formatDate(
                              pendingJoinRequest.requested_timestamp,
                            ),
                          })}
                        </Typography>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleCancelRequest}
                          fullWidth
                          sx={{ borderRadius: 3 }}
                        >
                          {t(SuccessStringKeys.CANCEL_REQUEST)}
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        sx={actionButtonStyle}
                        variant="contained"
                        onClick={() => {
                          setShowUpdateOrgModal(true);
                        }}
                        fullWidth
                      >
                        {t(StringKeys.CHANGE_ORGANIZATION)}
                      </Button>
                    )}
                  </Grid>
                  <Grid size={12}>
                    {isAdmin(orgUser?.role) && (
                      <Grid size={12}>
                        <Button
                          sx={actionButtonStyle}
                          variant="contained"
                          onClick={() => setShowUpdateGroupModal(true)}
                          fullWidth
                        >
                          {t(StringKeys.CHANGE_GROUP)}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Personal Information */}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Paper elevation={3} sx={{ borderRadius: '12px', height: '100%' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: COLORS.TABLE_HEADER_GRAY,
                  color: COLORS.SAFEGUARD_BLUE,
                  borderRadius: '12px 12px 0 0',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                <PersonIcon />
                <Typography variant="h6">
                  {t(StringKeys.PERSONAL_DETAILS)}
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t(StringKeys.PHONE_NUMBER)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ mt: 0.5, color: COLORS.SAFEGUARD_BLUE }}
                    >
                      {orgUser?.phone_number}
                    </Typography>
                  </Grid>
                  <Grid size={12}>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        setConfirmTitle(t(StringKeys.DELETE_ACCOUNT));
                        setConfirmMessage(t(StringKeys.CONFIRM_DELETE_ACCOUNT));
                        setShowConfirmModal(true);
                      }}
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      {t(StringKeys.DELETE_ACCOUNT)}
                    </Button>
                  </Grid>
                  <Grid size={12}>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={() => {
                        downloadMyData();
                      }}
                      fullWidth
                      sx={actionButtonStyle}
                    >
                      {t(StringKeys.DOWNLOAD_MY_DATA)}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Settings Section */}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Paper elevation={2} sx={{ borderRadius: '12px', height: '100%' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: COLORS.TABLE_HEADER_GRAY,
                  color: COLORS.SAFEGUARD_BLUE,
                  borderRadius: '12px 12px 0 0',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                <SettingsIcon />
                <Typography variant="h6">
                  {t(StringKeys.SETTINGS_AND_PREFERENCES)}
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {t(StringKeys.LANGUAGE)}
                    </Typography>
                    <LanguageSelector
                      value={language}
                      onChange={(e: SelectChangeEvent) =>
                        changeLanguage(e.target.value)
                      }
                      size="small"
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t(StringKeys.WEB_APP_VERSION)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ mt: 0.5, color: COLORS.SAFEGUARD_BLUE }}
                    >
                      {versionNumber}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Documentation Section */}
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Paper elevation={2} sx={{ borderRadius: '12px', height: '100%' }}>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: COLORS.TABLE_HEADER_GRAY,
                  color: COLORS.SAFEGUARD_BLUE,
                  borderRadius: '12px 12px 0 0',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                <ArticleIcon />
                <Typography variant="h6">
                  {t(StringKeys.DOCUMENTATION)}
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Button
                      onClick={() =>
                        window.open(SupportLinks.PRIVACY_POLICY, '_blank')
                      }
                    >
                      {t(StringKeys.PRIVACY_POLICY)}
                    </Button>
                  </Grid>
                  <Grid size={12}>
                    <Button onClick={() => setShowPermissionsModal(true)}>
                      {t(StringKeys.BROWSER_PERMISSIONS)}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  if (!organization || !orgUser || !userPrivateSettings || !userHistory) return;

  return (
    <Container maxWidth="xl">
      <>
        {!loading ? (
          orgUser && userPrivateSettings && organization ? (
            <>
              {renderData()}
              <EditProfileNameModal
                show={showEditProfileModal}
                onHide={() => setShowEditProfileModal(false)}
                saveUserName={saveUserName}
              />
            </>
          ) : (
            <LoadingSpinner />
          )
        ) : (
          <LoadingSpinner />
        )}
      </>
      <PermissionsModal
        show={showPermissionsModal}
        onHide={() => setShowPermissionsModal(false)}
      />
      <ConfirmModal
        show={showConfirmModal}
        confirm={deleteProfile}
        onHide={() => setShowConfirmModal(false)}
        confirmMessage={confirmMessage}
        confirmTitle={confirmTitle}
      />
      <UpdateOrganizationModal
        show={showUpdateOrgModal}
        onHide={() => setShowUpdateOrgModal(false)}
        currentOrgData={{
          org_code: organization.org_code,
          old_organization_azure_id: organization.id,
          org_user_azure_id: orgUser.id,
          user_history_azure_id: userHistory.id,
          user_private_settings_azure_id: userPrivateSettings.id,
        }}
        onPendingApproval={handlePendingApproval}
      />
      <UpdateGroupModal
        show={showUpdateGroupModal}
        onHide={() => setShowUpdateGroupModal(false)}
        currentOrgData={{
          org_code: organization.org_code,
          auth_id: orgUser.auth_id,
          organization_azure_id: organization.id,
        }}
        initialGroups={getGroupNames(organization)}
      />
      <LoadingData show={loadingAPIData} />
    </Container>
  );
};

const AuthenticatedProfile = withAuthenticationRequired(Profile, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});

export default AuthenticatedProfile;
